import { Button, deepMerge, Text } from '@mantine/core';
import { IconCheck, IconMail, IconX } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { contractsApi } from '../../lib/apis/contracts';
import { useContractsQueries } from '../../lib/queries/contracts';
import { ValidationForm } from '../../lib/types/validation';

import {
  filterOrganizationColumns,
  Permission,
  usePermissions,
} from '@auth/data-access';
import {
  FORM_STATUS,
  useFiltersStore,
  useFiltersTableOptions,
} from '@data-access';
import {
  formatOrganizationNames,
  OrganizationsList,
  PfTable,
  StatusCell,
  StatusColumnFilter,
  TsrButtonLink,
  useRowSelection,
} from '@ui';

interface ValidationFormsTableProps {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
}

export const ValidationFormsTable: FC<ValidationFormsTableProps> = ({
  rowSelection,
  setRowSelection,
}) => {
  const { t } = useTranslation();
  const { can, showOrganizationColumn } = usePermissions();
  const contractsQueries = useContractsQueries();
  const isAdmin = can(Permission.ManageContracts);
  const page =
    useFiltersStore.use['validationForms']().pagination.pageIndex + 1;
  const sorting = useFiltersStore.use['validationForms']().sorting;
  const filters = useFiltersStore.use['validationForms']().filters;
  const setFilters = useFiltersStore((state) => state.setFilters);
  const filtersTableOptions =
    useFiltersTableOptions<ValidationForm>('validationForms');

  const validationForms = useQuery({
    ...contractsQueries.validationFormsList({ page, sorting, filters }),
    placeholderData: keepPreviousData,
  });

  const rowSelectionProps = useRowSelection<ValidationForm>({
    data: validationForms.data?.results ?? [],
    rowSelection,
    setRowSelection,
  });

  const sendReminderMail = useMutation({
    mutationFn: contractsApi.validationForms.sendReminderMail,
    onSuccess: () => {
      validationForms.refetch();
      setRowSelection({});
    },
  });

  const handleSendReminderMail = () => {
    if (rowSelection.all) {
      sendReminderMail.mutate({
        ids: [],
        all: true,
        selectedFilters: filters,
      });
    } else {
      const ids = Object.keys(rowSelection)
        .filter((id) => id !== 'all')
        .map((id) => Number(id));
      if (ids.length > 0) {
        sendReminderMail.mutate({ ids });
      }
    }
  };

  const columns = useMemo<MRT_ColumnDef<ValidationForm>[]>(
    () =>
      filterOrganizationColumns(
        [
      {
        accessorKey: 'contract.farm.name',
        header: t('common.company_name'),
        Cell: ({ cell, row }) => {
          const farmName = cell.getValue<string>();
          if (isAdmin) {
            return (
              <TsrButtonLink
                fullWidth
                justify="start"
                maw={250}
                ml="calc(var(--button-padding-x-compact-md) * -1)"
                params={{ validationFormId: row.original.id.toString() }}
                size="compact-md"
                td="underline"
                to="/os/validation-forms/$validationFormId"
                tt="capitalize"
                variant="subtle"
              >
                <Text span truncate="end">
                  {farmName}
                </Text>
              </TsrButtonLink>
            );
          }
          return (
            <Text span truncate="end">
              {farmName}
            </Text>
          );
        },
      },
      {
        id: 'organizations',
        accessorFn: (row) => formatOrganizationNames(row.organizations),
        header: t('common.properties.organization'),
        Cell: ({ row }) => (
          <OrganizationsList organizations={row.original.organizations} />
        ),
      },
      {
        accessorKey: 'account.last_name',
        header: t('common.properties.last_name'),
        accessorFn: (row) => `${row.account.last_name} ${row.account.first_name}`,
      },
      {
        accessorKey: 'account.phone_number',
        header: t('common.properties.phone_number'),
        Cell: ({ cell }) => {
          const phoneNumber = cell.getValue<string>();
          return phoneNumber && phoneNumber !== 'nan' ? phoneNumber : '-';
        },
      },
    //   {
    //     accessorKey: 'contract.clause',
    //     header: t('common.properties.clause'),
    //     Cell: ({ cell }) => {
    //       const clause = cell.getValue<string>();
    //       return t(`clauses.${clause}`);
    //     },
    //   },
    //   {
    //     accessorFn: (row) => row.contract.consent_form?.status,
    //     id: 'contract.consent_form.status',
    //     header: t('common.properties.consent_form_status'),
    //     maxSize: 100,
    //     Cell: ({ cell, row }) => {
    //       const status = row.original.contract.consent_form?.status;
    //       if (!status) {
    //         return '-';
    //       }
    //       return <StatusCell status={status} />;
    //     },
    //     Filter: ({ column }) => {
    //       return (
    //         <StatusColumnFilter
    //           onChange={(value) => column.setFilterValue(value)}
    //           onClear={() => column.setFilterValue(null)}
    //           value={column.getFilterValue() as FORM_STATUS}
    //         />
    //       );
    //     },
    //   },
      {
        accessorKey: 'status',
        header: t('common.properties.status'),
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          if (!status) {
            return '-';
          }
          return <StatusCell status={status as FORM_STATUS} />;
        },
        Filter: ({ column }) => {
          return (
            <StatusColumnFilter
              onChange={(value) => column.setFilterValue(value)}
              onClear={() => column.setFilterValue(null)}
              value={column.getFilterValue() as FORM_STATUS}
            />
          );
        },
      },
      {
        accessorKey: 'reminder1_sent',
        Cell: ({ cell }) => {
          const sent = cell.getValue<boolean>();
          return sent ? (
            <IconCheck color="green" size={16} />
          ) : (
            <IconX color="red" size={16} />
          );
        },
        filterVariant: 'checkbox',
        header: t('common.properties.reminder1_sent'),
        maxSize: 80,
        size: 60,
      },
      {
        accessorKey: 'reminder2_sent',
        Cell: ({ cell }) => {
          const sent = cell.getValue<boolean>();
          return sent ? (
            <IconCheck color="green" size={16} />
          ) : (
            <IconX color="red" size={16} />
          );
        },
        filterVariant: 'checkbox',
        header: t('common.properties.reminder2_sent'),
        maxSize: 80,
        size: 60,
      },
      {
        accessorKey: 'reminder3_sent',
        Cell: ({ cell }) => {
          const sent = cell.getValue<boolean>();
          return sent ? (
            <IconCheck color="green" size={16} />
          ) : (
            <IconX color="red" size={16} />
          );
        },
        filterVariant: 'checkbox',
        header: t('common.properties.reminder3_sent'),
        maxSize: 80,
        size: 60,
      },
        ],
        showOrganizationColumn('list'),
      ),
    [t, isAdmin, showOrganizationColumn],
  );

  const tableOptions: MRT_TableOptions<ValidationForm> = deepMerge(
    {
      data: validationForms.data?.results ?? [],
      rowCount: validationForms.data?.count ?? 0,
      columns,

      state: {
        showLoadingOverlay:
          validationForms.isFetching && validationForms.isPlaceholderData,
        rowSelection,
      },

      initialState: {
        showColumnFilters: true,
      },

      mantinePaperProps: {
        h: '100%',
      },

      renderTopToolbarCustomActions: ({ table }) => (
        <Button
          disabled={Object.keys(rowSelection).length === 0}
          leftSection={<IconMail size={16} />}
          loading={sendReminderMail.isPending}
          onClick={handleSendReminderMail}
        >
          {t('common.send_reminder_mail')}
        </Button>
      ),
      ...rowSelectionProps,
    },
    filtersTableOptions,
  );
  tableOptions.onColumnFiltersChange = (updater) => {
    const next = typeof updater === 'function' ? updater(filters) : updater;
    const scope = filters.filter(
      (filter) => filter.id === 'clause' || filter.id === 'campaign_year',
    );
    const columns = next.filter(
      (filter) => filter.id !== 'clause' && filter.id !== 'campaign_year',
    );
    setFilters('validationForms', [...scope, ...columns]);
  };

  return <PfTable<ValidationForm> options={tableOptions} />;
};

export default ValidationFormsTable;
